import { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, TEOLoader } from '@teo/components';
import XyflowComponent from '@/components/LearningPath/XyflowComponent';
import SettingsNode from '@/components/LearningPath/SettingsNode';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { useNodesState, useEdgesState, MarkerType } from '@xyflow/react';
import { axios } from '@/axios';
import { config } from '@/config';
import { newUid } from '@/util';
import {
  nodeListTree,
  edgeListTree,
  cleanConection,
  sortDataLP,
} from '@/components/LearningPath/util';
import { useMediaQuery } from 'react-responsive';
import { ArrowRight, ArrowLeft } from '@teo/components/icons';

const EditSectionLearningPath = ({
  learningpathId,
  learningpath,
  setImage,
  image,
  allNodes,
  categories,
  allCategories,
  setLearningpath,
  isOwner,
  refetchCorrection,
  misingConnect,
  setMisingConnect,
  setNewLpCompetencies,
  newLpCompetencies,
  setLpCompetencies,
  lpCompetencies,
  refetchCompetencies,
}: any) => {
  const { t } = useTranslation();
  const [nodeSelected, setNodeSelected] = useState<any>(null);
  const { auth } = useContext(AuthContext);
  const authOrg: any = auth?.user?.activeOrganisationId;
  const authUser = auth?.user;

  const [isDraggingDisabled, setIsDraggingDisabled] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoaderOpen, setIsLoaderOpen] = useState(false);

  const [showSettings, setShowSettings] = useState(false);

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  function mergeById(source: any, target: any) {
    const targetMap = new Map(
      target.map((item: any) => [String(item.id), item])
    );

    return source.map((item: any) => {
      const match = targetMap.get(String(item.id));
      return match
        ? { ...item, data: { ...item?.data, notConnect: true } }
        : item;
    });
  }

  const setThemeNodeSelected = (item: any) => {
    setNodeSelected({
      id: item?.id,
      type: 'customNode',
      data: {
        learningPathId: item?.learningpathId,
        blocking: item?.blocking,
        mandatory: item?.mandatory,
        notCompleted: item?.notCompleted,
        notPassed: item?.notPassed,
        image: item?.picture,
        roots: item?.roots,
        rootDescriptions: item?.rootDescriptions,
        courseType: item?.subType,
        nodeID: item?.objectId,
        type: item?.type,
        tags: item?.tags,
        trainingId: item?.type === 'training' && item?.objectId,
        courseId: item?.type === 'course' && item?.objectId,
        title: item?.name,
      },
    });
  };

  const setLpNodes = async () => {
    const nodeRoot = allNodes?.find((el: any) => el?.type === 'root');
    const nodedetails = allNodes?.filter((el: any) => el?.type !== 'root');

    if (allNodes?.length > 0) {
      const sortData = sortDataLP(nodedetails);
      const nodesList =
        (await nodeListTree(
          sortData,
          authOrg,
          handleRemoveNode,
          handleReplaceNode,
          handleMakeTheme,
          setThemeNodeSelected,
          setIsDraggingDisabled,
          allCategories,
          learningpathId
        )) || [];

      const updatedNodesList: any = nodesList?.concat({
        id: `${nodeRoot?.id}`,
        type: 'customNode',
        deletable: false,
        position: {
          x: nodeRoot?.x ? nodeRoot?.x : 0,
          y: nodeRoot?.y ? nodeRoot?.y : 0,
        },
        data: {
          title: learningpath?.lpName,
          type: 'homepage',
          image: image,
          description: learningpath?.lpDescription,
          courseType: null,
        },
      });
      const edgesList: any = edgeListTree(sortData);

      setNodes(updatedNodesList);
      setEdges(edgesList);
    } else {
      const homeTree: any = [
        {
          id: `${nodeRoot?.id}`,
          type: 'customNode',
          position: {
            x: nodeRoot?.x ? nodeRoot?.x : 0,
            y: nodeRoot?.y ? nodeRoot?.y : 0,
          },
          data: {
            title: learningpath?.lpName,
            type: 'homepage',
            image: image,
            description: learningpath?.lpDescription,
            courseType: null,
          },
        },
        {
          id: newUid(10),
          type: 'customNodeEmpty',
          position: {
            x: nodeRoot?.x ? nodeRoot?.x : 0,
            y: nodeRoot?.y ? nodeRoot?.y + 300 : 300,
          },
          data: { handlePosition: 'bottom', empty: true },
        },
      ];
      const homeEdges: any = [
        {
          id: newUid(10),
          source: homeTree[0]?.id,
          target: homeTree[1]?.id,
          sourceHandle: 'd',
          type: 'smoothstep',
          markerEnd: { type: MarkerType.Arrow },
        },
      ];
      setNodes(homeTree);
      setEdges(homeEdges);
    }
  };

  useEffect(() => {
    if (allNodes) {
      setLpNodes();
    }
  }, [allNodes]);

  useEffect(() => {
    const nodedetails = allNodes?.filter((el: any) => el?.type !== 'root');
    if (nodedetails?.length > 0) {
      if (misingConnect?.length > 0) {
        const result = mergeById(nodes, misingConnect);
        setNodes(result);
      } else {
        const cleaned: any = nodes.map((item: any) => {
          if (!item?.data || !('notConnect' in item.data)) return item;

          const { notConnect, ...dataRest } = item.data;
          return { ...item, data: dataRest };
        });
        setNodes(cleaned);
      }
    }
  }, [misingConnect]);

  const handleChangeNode = (
    id: any,
    change: any,
    lpID: any = learningpathId
  ) => {
    axios.put(`${config.backend}/learningpathnode/${lpID}/${id}`, change);
  };

  const handleRemoveNode = (
    node: any,
    prevNodes = nodes,
    prevEdges = edges
  ) => {
    const updateNodeConect = cleanConection('node', node, prevNodes, prevEdges);
    setNodes((nds: any) => nds?.filter((el: any) => el.id !== node.id));

    setEdges((eds: any) =>
      eds?.filter(
        (edge: any) => edge.source !== node.id && edge.target !== node.id
      )
    );
    if (node?.type !== 'customNodeEmpty') {
      if (node?.data?.type === 'course') {
        axios.delete(
          `${config.backend}/learningpathnode/${learningpathId}/${node?.id}`
        );
      }
      if (node?.data?.type === 'training') {
        axios.delete(
          `${config.backend}/learningpathnode/${learningpathId}/${node?.id}`
        );
      }
      if (node?.data?.type === 'learningpath') {
        axios.delete(
          `${config.backend}/learningpathnode/${learningpathId}/${node?.id}`
        );
      }
    }

    if (updateNodeConect?.length > 0) {
      updateNodeConect?.map((el: any) =>
        handleChangeNode(el?.node?.id, {
          roots: el?.rootsId,
          rootDescriptions: el?.rootsConect,
        })
      );
    }
  };

  const handleRemoveChildNode = (rmNode: any, prevNodes = nodes) => {
    const parentLpId = rmNode?.data?.learningPathId;
    const findTheme: any = prevNodes?.find(
      (nds: any) => nds?.data?.nodeID === parentLpId
    );
    const updateNodeChild = findTheme?.data?.child?.filter(
      (ch: any) => ch?.id !== rmNode?.id
    );

    const updatedData = updateNodeChild?.map((item: any) => {
      return {
        ...item,
        roots: item.roots
          .map((root: any) => (root == rmNode?.id ? rmNode?.data?.roots : root))
          .flat(),
      };
    });
    const updateNode: any = prevNodes?.map((node: any) =>
      node?.data?.nodeID == parentLpId
        ? { ...node, data: { ...node?.data, child: updatedData } }
        : node
    );

    const updatedObjects = updateNodeChild
      ?.map((item: any) => {
        const newRoots = item.roots
          .map((root: any) => (root == rmNode?.id ? rmNode?.data?.roots : root))
          .flat();

        if (JSON.stringify(newRoots) !== JSON.stringify(item.roots)) {
          return { ...item, roots: newRoots };
        }
        return null;
      })
      .filter((item: any) => item !== null);

    if (updatedObjects?.length > 0) {
      updatedObjects?.map((el: any) =>
        axios.put(
          `${config.backend}/learningpathnode/${parentLpId}/${el?.id}`,
          {
            roots: el?.roots,
          }
        )
      );
    }

    if (rmNode?.id) {
      axios.delete(
        `${config.backend}/learningpathnode/${parentLpId}/${rmNode?.id}`
      );
    }
    setNodes(updateNode);
    setNodeSelected(null);
  };

  const handleReplaceNode = (node: any, prevNodes = nodes) => {
    const updateNodes: any = prevNodes?.map((nd: any) =>
      nd?.id === node
        ? {
            ...nd,
            type: 'customNodeEmpty',
            data: {
              ...nd.data,
              empty: true,
              handleRemoveNode: handleRemoveNode,
              handlePosition: 'all',
              replace: nd?.id,
            },
          }
        : nd
    );
    setNodes(updateNodes);
    setNodeSelected(updateNodes?.find((nds: any) => nds?.id === node));
  };

  function removeItems(array: any, delArray: any) {
    const removeIds = new Set(
      delArray.slice(1).map((nds: any) => String(nds.id))
    );

    return array.filter((item: any) => !removeIds.has(String(item.id)));
  }

  function orderSelectedByGraph(nodes: any, edges: any, selected: any) {
    const selectedIds = new Set(selected.map((n: any) => String(n.id)));

    const indegree = new Map();

    for (const n of selected) indegree.set(String(n.id), 0);

    for (const e of edges) {
      if (
        selectedIds.has(String(e.source)) &&
        selectedIds.has(String(e.target))
      ) {
        indegree.set(String(e.target), indegree.get(String(e.target)) + 1);
      }
    }

    const queue = selected
      .filter((n: any) => indegree.get(String(n.id)) === 0)
      .map((n: any) => String(n.id));

    const byId = new Map(selected.map((n: any) => [String(n.id), n]));
    const result = [];

    while (queue.length) {
      const id = queue.shift();
      const node = byId.get(id);
      if (!node) continue;

      result.push(node);

      for (const e of edges) {
        if (String(e.source) === id && selectedIds.has(String(e.target))) {
          const tid = String(e.target);
          indegree.set(tid, indegree.get(tid) - 1);
          if (indegree.get(tid) === 0) queue.push(tid);
        }
      }
    }

    return result;
  }

  const edgeSig = (s: any, t: any) => `${s}→${t}`;

  const buildIO = (edges: any) => {
    const map = new Map();
    for (const e of edges) {
      if (!map.has(e.source)) map.set(e.source, { in: [], out: [] });
      if (!map.has(e.target)) map.set(e.target, { in: [], out: [] });

      map.get(e.source).out.push(e.target);
      map.get(e.target).in.push(e.source);
    }
    return map;
  };

  const buildHandleMap = (edges: any) => {
    const map = new Map();
    for (const e of edges) {
      map.set(edgeSig(e.source, e.target), {
        s: e.sourceHandle,
        t: e.targetHandle,
      });
    }
    return map;
  };

  const handleMakeTheme = async (
    node: any,
    prevNodes = nodes,
    prevEdges = edges
  ) => {
    const rawSelected = prevNodes.filter((n: any) => n.selected);
    const allSelectNodes: any = orderSelectedByGraph(
      prevNodes,
      prevEdges,
      rawSelected
    );

    const waitForReact = () => new Promise(requestAnimationFrame);

    const newLP = await axios.post(`${config.backend}/learningpaths`, {
      lpAccess: 'protected',
      lpName: 'New Learning path',
      lpIsRoot: false,
    });
    const newLearningpathId = newLP?.data?.id;

    await axios.post(`${config.backend}/memberlearningpaths`, {
      learningpathId: newLearningpathId,
      organisationId: authOrg,
    });

    await handleChangeNode(allSelectNodes[0]?.id, {
      objectId: newLearningpathId,
      picture: null,
      subType: 'learningpath',
      type: 'learningpath',
    });

    const rootNodes = await axios.get(
      `${config.backend}/learningpathnodedetails?learningpathId=${newLearningpathId}&organisationId=${authOrg}`
    );

    const rootNode = rootNodes?.data[0];

    let currentRootId = rootNode.id;
    const themeNodes: any = [];

    for (const node of allSelectNodes as any) {
      const res = await axios.post(`${config.backend}/learningpathnodes`, {
        learningpathId: newLearningpathId,
        objectId: node?.data?.nodeID,
        type: node?.data?.type,
        subType: node?.data?.courseType,
        x: node?.position?.x ?? 0,
        y: node?.position?.y ?? 0,
        roots: [currentRootId],
        rootDescriptions: ['d/b'],
      });
      const newNode = { ...res.data, name: node.data?.title };
      themeNodes?.push(newNode);

      const createdId = res.data.id;

      currentRootId = createdId;
    }

    const IDS = allSelectNodes?.map((el: any) => el.id);
    const REPLACEMENT = allSelectNodes[0]?.id;

    const idSet = new Set(IDS);

    const stableEdges = prevEdges.filter((e: any) => {
      const s = idSet.has(e.source);
      const t = idSet.has(e.target);
      return !s && !t;
    });

    const collapsedEdges = Object.values(
      prevEdges.reduce((acc: any, e: any) => {
        const sourceIn = idSet.has(e.source);
        const targetIn = idSet.has(e.target);

        if (sourceIn && targetIn) return acc;

        if (!sourceIn && !targetIn) return acc;

        const newSource = sourceIn ? REPLACEMENT : e.source;
        const newTarget = targetIn ? REPLACEMENT : e.target;

        const key = `${newSource}→${newTarget}`;
        if (acc[key]) return acc;

        acc[key] = { ...e, id: key, source: newSource, target: newTarget };
        return acc;
      }, {})
    );

    const finalEdges: any = [...stableEdges, ...collapsedEdges];

    setEdges(finalEdges);

    await waitForReact();

    for (let i = 0; i < allSelectNodes.length; i++) {
      const node = allSelectNodes[i];
      if (i > 0) {
        setNodes((nds: any) => nds?.filter((el: any) => el.id !== node.id));

        if (node?.type !== 'customNodeEmpty') {
          if (node?.data?.type === 'course') {
            axios.delete(
              `${config.backend}/learningpathnode/${learningpathId}/${node?.id}`
            );
          }
          if (node?.data?.type === 'training') {
            axios.delete(
              `${config.backend}/learningpathnode/${learningpathId}/${node?.id}`
            );
          }
          if (node?.data?.type === 'learningpath') {
            axios.delete(
              `${config.backend}/learningpathnode/${learningpathId}/${node?.id}`
            );
          }
        }

        await waitForReact();
      }
    }

    const beforeIO = buildIO(prevEdges);
    const afterIO = buildIO(finalEdges);

    const oldHandles = buildHandleMap(prevEdges);
    const newHandles = buildHandleMap(finalEdges);

    const allNodes = new Set([...beforeIO.keys(), ...afterIO.keys()]);

    const rewires = [];

    for (const node of allNodes) {
      const b = beforeIO.get(node) || { in: [], out: [] };
      const a = afterIO.get(node) || { in: [], out: [] };

      const inRemoved = b.in.filter((x: any) => !a.in.includes(x));
      const inAdded = a.in.filter((x: any) => !b.in.includes(x));
      const outRemoved = b.out.filter((x: any) => !a.out.includes(x));
      const outAdded = a.out.filter((x: any) => !b.out.includes(x));

      if (
        inRemoved.length ||
        inAdded.length ||
        outRemoved.length ||
        outAdded.length
      ) {
        rewires.push({
          node,
          before: b,
          after: a,
          inRemoved,
          inAdded,
          outRemoved,
          outAdded,
        });
      }
    }

    const updates = [];

    for (const r of rewires) {
      if (!r.inAdded.length && !r.inRemoved.length) continue;

      const target = r.node;
      const afterSources = r.after.in;

      const roots = [];
      const rootDescriptions = [];

      for (const src of afterSources) {
        const key = edgeSig(src, target);
        const h = newHandles.get(key);

        if (!h) continue; // safety

        roots.push(src);
        rootDescriptions.push(`${h.s}/${h.t}`);
      }

      updates.push({
        id: target,
        payload: {
          roots,
          rootDescriptions,
        },
      });
    }

    for (const u of updates as any) {
      if (u.payload?.roots?.length > 0) {
        await axios.put(
          `${config.backend}/learningpathnode/${learningpathId}/${u.id}`,
          u.payload
        );
      }
    }

    if (themeNodes?.length > 0 && newLearningpathId) {
      const filtered = removeItems(prevNodes, allSelectNodes);

      const updateNodes: any = filtered?.map((nd: any) =>
        nd?.id === allSelectNodes[0]?.id
          ? {
              ...nd,
              data: {
                ...nd.data,
                tags: null,
                title: 'New Learning path',
                nodeID: newLearningpathId,
                type: 'learningpath',
                courseType: 'learningpath',
                publicationCourseType: null,
                image: null,
                courseId: false,
                trainingId: false,
                coursecontentId: null,
                child: [rootNode, ...themeNodes],
                setIsDraggingDisabled: setIsDraggingDisabled,
              },
            }
          : nd
      );
      setNodes(updateNodes);
      setNodeSelected(
        updateNodes?.find((nds: any) => nds?.id === allSelectNodes[0]?.id)
      );
    }
  };

  return (
    <div
      className={`relative flex h-full w-full ${
        isMobile && 'overflow-hidden'
      } max-h-[calc(100vh_-_150px)]`}
    >
      {isMobile && (
        <div className="absolute top-2 right-2 z-[10]">
          <IconButton
            Icon={showSettings ? ArrowRight : ArrowLeft}
            variant="outline"
            className="bg-grey-02"
            onClick={() => setShowSettings((prev) => !prev)}
          />
        </div>
      )}
      {isLoaderOpen && (
        <div className="fixed inset-0 z-[101] flex bg-white/75">
          <div className="m-auto flex flex-col">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[100px]"
              text="SCORM is loading ..."
            />
          </div>
        </div>
      )}
      <div className={`flex-auto ${isMobile && 'min-h-[calc(100vh_-_126px)]'}`}>
        <XyflowComponent
          learningpathId={learningpathId}
          setNodeSelected={setNodeSelected}
          onNodesChange={onNodesChange}
          setNodes={setNodes}
          nodes={nodes}
          onEdgesChange={onEdgesChange}
          setEdges={setEdges}
          edges={edges}
          handleChangeNode={handleChangeNode}
          handleRemoveNode={handleRemoveNode}
          setIsDraggingDisabled={setIsDraggingDisabled}
          isDraggingDisabled={isDraggingDisabled}
          handleRemoveChildNode={handleRemoveChildNode}
          setMisingConnect={setMisingConnect}
        />
      </div>
      <div
        className={`w-full ${
          isMobile
            ? showSettings
              ? 'absolute left-0 top-0 bottom-0 z-[9] min-h-[calc(100vh_-_126px)] max-w-[100vw] bg-white'
              : 'absolute left-[100%] top-0 bottom-0 z-[9] min-h-[calc(100vh_-_126px)] max-w-[100vw] bg-white'
            : 'relative min-h-[calc(100vh_-_150px)] max-w-[362px] border-l border-solid border-grey-02'
        }`}
      >
        <SettingsNode
          key={nodeSelected?.id + '_' + nodeSelected?.data?.courseId}
          learningpathId={learningpathId}
          learningpath={learningpath}
          setLearningpath={setLearningpath}
          setIsLoaderOpen={setIsLoaderOpen}
          nodeSelected={nodeSelected}
          setNodeSelected={setNodeSelected}
          setThemeNodeSelected={setThemeNodeSelected}
          authUser={authUser}
          handleChangeNode={handleChangeNode}
          handleRemoveNode={handleRemoveNode}
          handleRemoveChildNode={handleRemoveChildNode}
          handleReplaceNode={handleReplaceNode}
          handleMakeTheme={handleMakeTheme}
          setIsDraggingDisabled={setIsDraggingDisabled}
          setNodes={setNodes}
          nodes={nodes}
          setEdges={setEdges}
          edges={edges}
          setHomeImage={setImage}
          homeImage={image}
          categories={categories}
          allCategories={allCategories}
          refetchCorrection={refetchCorrection}
          isOwner={isOwner}
          setNewLpCompetencies={setNewLpCompetencies}
          newLpCompetencies={newLpCompetencies}
          setLpCompetencies={setLpCompetencies}
          lpCompetencies={lpCompetencies}
          refetchCompetencies={refetchCompetencies}
        />
      </div>
    </div>
  );
};

export default EditSectionLearningPath;
